/**
 * Edit Plans batch matrix — business-logic hook
 *
 * Per-member tier + add-on edits collected in a Map keyed by userId. Effective
 * state for any member = edit (if present) ∪ original (member.bundles).
 * Commit walks the edits sequentially, calling per-member mutations. Errors on
 * any member surface a snackbar but DO NOT abort the run — the remaining
 * members still get processed.
 */

import { useCallback, useMemo, useState } from 'react';
import type { BundleWithStatus } from '@features/subscriptions';
import {
  getAddOnsIncludedInTier,
  getMonthlyAmountCents,
  useBundleCatalog,
  useGetFullSubscriptionDetailsQuery,
} from '@features/subscriptions';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useAddMemberBundleMutation,
  useChangeMemberBundleMutation,
  useRemoveMemberBundleMutation,
} from '../../store/api';
import type { OrganizationMemberTableRow } from '../../types';

export type BillingIntervalChoice = 'month' | 'year';

export interface MemberEdit {
  tierId: string;
  addOnIds: Set<string>;
}

export interface MemberOriginal {
  tierId: string;
  addOnIds: Set<string>;
}

interface UseEditPlansArgs {
  members: OrganizationMemberTableRow[];
  open: boolean;
  onSuccess: () => void;
}

const setsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every(id => b.has(id));

export function useEditPlans({ members, open, onSuccess }: UseEditPlansArgs) {
  const { enqueueSnackbar } = useSnackbar();
  const { practice } = useEventTracking();

  const {
    tiers,
    addOns,
    isLoading: isCatalogLoading,
  } = useBundleCatalog();
  const { data: subscriptionDetails } = useGetFullSubscriptionDetailsQuery();
  const orgBillingInterval: BillingIntervalChoice =
    subscriptionDetails?.billing_interval === 'year' ? 'year' : 'month';
  const isTrialing = !!subscriptionDetails?.is_trialing;

  // Trial-only: local preview interval the user can flip to see annual vs.
  // monthly equivalent pricing. Display-only — does not change mutation
  // payloads (billing cycle commitment is a separate org-level setting that
  // converts at trial end).
  const [previewInterval, setPreviewInterval] = useState<BillingIntervalChoice>(
    orgBillingInterval,
  );
  const effectiveInterval: BillingIntervalChoice = isTrialing
    ? previewInterval
    : orgBillingInterval;

  // Per-member edits. Keyed by userId. Absence = no pending change for that
  // member; presence = edit overlay applied on top of member.bundles.
  const [edits, setEdits] = useState<Map<string, MemberEdit>>(new Map());

  // ── Per-member original state derived from member.bundles ──────────────────
  const originalsByMember = useMemo(() => {
    const map = new Map<string, MemberOriginal>();
    for (const member of members) {
      const tier = member.bundles?.find(b => b.bundle_type === 'tier' && b.status === 'active');
      const addOnIds = new Set(
        member.bundles
          ?.filter(b => b.bundle_type === 'add_on' && b.status === 'active')
          .map(b => b.bundle_id) || [],
      );
      map.set(member.userId, { tierId: tier?.bundle_id || '', addOnIds });
    }
    return map;
  }, [members]);

  const getEffectiveState = useCallback(
    (userId: string): MemberOriginal => {
      const edit = edits.get(userId);
      if (edit) return { tierId: edit.tierId, addOnIds: edit.addOnIds };
      return originalsByMember.get(userId) ?? { tierId: '', addOnIds: new Set() };
    },
    [edits, originalsByMember],
  );

  // ── Cost math (shared with Person Card) ────────────────────────────────────
  const computeMemberCost = useCallback(
    (
      tierId: string,
      addOnIds: Set<string>,
      interval: BillingIntervalChoice,
    ): { cents: number; currency: string } => {
      const tier = tiers.find(t => t.bundle_id === tierId);
      if (!tier) return { cents: 0, currency: 'usd' };
      const memberAddOnBundles: BundleWithStatus[] = Array.from(addOnIds)
        .map(id => addOns.find(a => a.bundle_id === id))
        .filter((b): b is BundleWithStatus => b != null);
      const includedIds = new Set(
        getAddOnsIncludedInTier(tier, memberAddOnBundles).map(a => a.bundle_id),
      );
      const paidAddOns = memberAddOnBundles.filter(a => !includedIds.has(a.bundle_id));
      const isAnnual = interval === 'year';
      const tierCents = getMonthlyAmountCents(tier, isAnnual);
      const addOnsCents = paidAddOns.reduce(
        (sum, a) => sum + getMonthlyAmountCents(a, isAnnual),
        0,
      );
      return { cents: tierCents + addOnsCents, currency: tier.currency };
    },
    [tiers, addOns],
  );

  // Add-on bundle_ids fully covered by a given tier's grants (display lock +
  // commit-time double-bill guard). Matches Person Card semantics.
  const includedByTier = useCallback(
    (tierId: string): Set<string> => {
      const tier = tiers.find(t => t.bundle_id === tierId);
      if (!tier) return new Set();
      return new Set(getAddOnsIncludedInTier(tier, addOns).map(a => a.bundle_id));
    },
    [tiers, addOns],
  );

  // ── Setters ────────────────────────────────────────────────────────────────
  const setMemberTier = useCallback(
    (userId: string, newTierId: string) => {
      setEdits(prev => {
        const next = new Map(prev);
        const effective = next.get(userId) ??
          originalsByMember.get(userId) ?? { tierId: '', addOnIds: new Set<string>() };
        // Drop add-ons that are now covered by the new tier — keeps the wire
        // payload from accidentally adding redundant assignments and matches
        // the lock state the UI is about to show.
        const newIncluded = includedByTier(newTierId);
        const filteredAddOns = new Set(
          [...effective.addOnIds].filter(id => !newIncluded.has(id)),
        );
        next.set(userId, { tierId: newTierId, addOnIds: filteredAddOns });
        return next;
      });
    },
    [originalsByMember, includedByTier],
  );

  const toggleMemberAddOn = useCallback(
    (userId: string, addOnId: string) => {
      setEdits(prev => {
        const next = new Map(prev);
        const effective = next.get(userId) ??
          originalsByMember.get(userId) ?? { tierId: '', addOnIds: new Set<string>() };
        const addOnIds = new Set(effective.addOnIds);
        if (addOnIds.has(addOnId)) addOnIds.delete(addOnId);
        else addOnIds.add(addOnId);
        next.set(userId, { tierId: effective.tierId, addOnIds });
        return next;
      });
    },
    [originalsByMember],
  );

  // ── Change detection ────────────────────────────────────────────────────────
  const isMemberChanged = useCallback(
    (userId: string): boolean => {
      const original = originalsByMember.get(userId);
      const effective = getEffectiveState(userId);
      if (!original) return false;
      if (original.tierId !== effective.tierId) return true;
      return !setsEqual(original.addOnIds, effective.addOnIds);
    },
    [originalsByMember, getEffectiveState],
  );

  const changedUserIds = useMemo(
    () => members.map(m => m.userId).filter(isMemberChanged),
    [members, isMemberChanged],
  );
  const hasChanges = changedUserIds.length > 0;

  // ── Totals ─────────────────────────────────────────────────────────────────
  const originalTotal = useMemo(() => {
    let cents = 0;
    for (const member of members) {
      const o = originalsByMember.get(member.userId);
      if (!o) continue;
      cents += computeMemberCost(o.tierId, o.addOnIds, effectiveInterval).cents;
    }
    return cents;
  }, [members, originalsByMember, computeMemberCost, effectiveInterval]);

  const newTotal = useMemo(() => {
    let cents = 0;
    for (const member of members) {
      const e = getEffectiveState(member.userId);
      cents += computeMemberCost(e.tierId, e.addOnIds, effectiveInterval).cents;
    }
    return cents;
  }, [members, getEffectiveState, computeMemberCost, effectiveInterval]);

  const totalCurrency =
    tiers.find(t => t.bundle_id === originalsByMember.values().next().value?.tierId)?.currency ||
    'usd';

  // ── Commit ─────────────────────────────────────────────────────────────────
  const [changeMemberBundle, { isLoading: isChangingBundle }] = useChangeMemberBundleMutation();
  const [addMemberBundle, { isLoading: isAddingBundle }] = useAddMemberBundleMutation();
  const [removeMemberBundle, { isLoading: isRemovingBundle }] = useRemoveMemberBundleMutation();
  const isSaving = isChangingBundle || isAddingBundle || isRemovingBundle;

  const reset = useCallback(() => {
    setEdits(new Map());
    setPreviewInterval(orgBillingInterval);
  }, [orgBillingInterval]);

  const applyChanges = useCallback(async () => {
    if (!hasChanges) return;

    let successCount = 0;
    let errorCount = 0;

    for (const member of members) {
      if (!isMemberChanged(member.userId)) continue;

      const original = originalsByMember.get(member.userId);
      const effective = getEffectiveState(member.userId);
      if (!original) continue;

      try {
        // Tier change first (atomic swap; falls through to add when no current).
        if (effective.tierId !== original.tierId) {
          const currentTierAssignment = member.bundles?.find(
            b => b.bundle_type === 'tier' && b.status === 'active',
          );
          if (original.tierId && effective.tierId && currentTierAssignment) {
            await changeMemberBundle({
              userId: member.userId,
              assignmentId: currentTierAssignment.assignment_id,
              adminAddBundleRequest: { bundle_id: effective.tierId },
            }).unwrap();
          } else if (effective.tierId) {
            await addMemberBundle({
              userId: member.userId,
              adminAddBundleRequest: { bundle_id: effective.tierId },
            }).unwrap();
          }
        }

        // Belt-and-suspenders against double-billing: don't add an add-on that
        // the effective tier already grants. The setter filters too, but the
        // backend has no server-side guard (confirmed in
        // bundle_subscription_service.py) — keep this here as well.
        const tierCovered = includedByTier(effective.tierId);
        const addOnsToAdd = [...effective.addOnIds].filter(
          id => !original.addOnIds.has(id) && !tierCovered.has(id),
        );
        const addOnsToRemove = [...original.addOnIds].filter(id => !effective.addOnIds.has(id));

        for (const addOnId of addOnsToAdd) {
          await addMemberBundle({
            userId: member.userId,
            adminAddBundleRequest: { bundle_id: addOnId },
          }).unwrap();
        }
        for (const addOnId of addOnsToRemove) {
          const assignment = member.bundles?.find(
            b => b.bundle_id === addOnId && b.bundle_type === 'add_on' && b.status === 'active',
          );
          if (assignment) {
            await removeMemberBundle({
              userId: member.userId,
              assignmentId: assignment.assignment_id,
            }).unwrap();
          }
        }

        successCount += 1;
      } catch (error) {
        errorCount += 1;
        const msg =
          error &&
          typeof error === 'object' &&
          'data' in error &&
          error.data &&
          typeof error.data === 'object' &&
          'message' in error.data
            ? String(error.data.message)
            : 'Update failed';
        enqueueSnackbar(`${member.name || member.userId}: ${msg}`, { variant: 'error' });
      }
    }

    practice.batchEditApplied({
      members_changed: changedUserIds.length,
      members_succeeded: successCount,
      members_errored: errorCount,
      cost_delta_cents: newTotal - originalTotal,
    });

    if (successCount > 0) {
      const verb = successCount === 1 ? 'member' : 'members';
      enqueueSnackbar(`Updated ${successCount} ${verb} successfully`, { variant: 'success' });
    }
    if (errorCount === 0) {
      reset();
      onSuccess();
    }
  }, [
    hasChanges,
    members,
    isMemberChanged,
    originalsByMember,
    getEffectiveState,
    includedByTier,
    changedUserIds.length,
    newTotal,
    originalTotal,
    changeMemberBundle,
    addMemberBundle,
    removeMemberBundle,
    enqueueSnackbar,
    practice,
    reset,
    onSuccess,
  ]);

  // Fire opened analytic on each open
  const reportOpened = useCallback(() => {
    practice.batchEditOpened({
      member_count: members.length,
      is_trialing: isTrialing,
      billing_interval: orgBillingInterval,
    });
  }, [practice, members.length, isTrialing, orgBillingInterval]);

  return {
    // catalog / context
    tiers,
    addOns,
    isCatalogLoading,
    isTrialing,
    orgBillingInterval,
    effectiveInterval,
    previewInterval,
    setPreviewInterval,
    // state
    edits,
    getEffectiveState,
    isMemberChanged,
    includedByTier,
    // totals + change detection
    hasChanges,
    changedUserIds,
    originalTotal,
    newTotal,
    totalCurrency,
    // setters
    setMemberTier,
    toggleMemberAddOn,
    // commit
    applyChanges,
    isSaving,
    reset,
    // misc
    computeMemberCost,
    reportOpened,
    open,
  };
}
